import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePageBar } from "../contexts/PageBarContext";
import { useTripName } from "../hooks/useTripName";
import { useEffect, useState, useMemo, useRef } from "react";
import { doc, onSnapshot, getDoc, updateDoc, deleteDoc, deleteField, serverTimestamp, Timestamp } from "firebase/firestore";
import { fireWrite } from "../utils/offlineWrites";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useEntities } from "../hooks/useEntities";
import { useLiveWaitTime } from "../hooks/useLiveWaitTime";
import { useParkWaitTimes } from "../hooks/useParkWaitTimes";
import { useActiveAsOf, nowWindow } from "../hooks/useActiveAsOf";
import { partitionByActive } from "../utils/entityActive";
import { EntityForm } from "../components/entities/EntityForm";
import { LiveWaitChip, ChildWaitBadge } from "../components/entities/LiveWaitChip";
import { ParkScheduleCard } from "../components/entities/ParkScheduleCard";
import { EntityHistory } from "../components/entities/EntityHistory";
import { THEMEPARKS_WIKI_SOURCE } from "../config/themeparksWiki";
import {
  createInteractionAt,
  buildTripContextSearch,
} from "../utils/activityLogging";
import { isPlaceInside } from "../utils/entityAncestry";
import type { Entity, EntityType, Interaction, Trip, Segment } from "../types";
import { withCurrentType } from "../types";

type EntityWithId = Entity & { id: string };

function typeLabel(type: string): string {
  return type.replace(/_/g, " ");
}

export function EntityDetail() {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();

  const tripContextTripId = searchParams.get("tripId");
  const tripContextSegmentId = searchParams.get("segmentId");
  // The place being logged inside of, carried through the drilldown so a ride
  // picked from inside a park still files under the park check-in.
  const tripContextParentId = searchParams.get("parentId");
  const inTripContext = !!tripContextTripId;
  const tripContextSearch = tripContextTripId
    ? buildTripContextSearch(tripContextTripId, tripContextSegmentId, tripContextParentId)
    : "";

  const [entity, setEntity] = useState<EntityWithId | null>(null);
  const [parentName, setParentName] = useState<string | null>(null);
  const contextTripName = useTripName(tripContextTripId ?? undefined);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logging, setLogging] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Trip-context: trip + segment names used to label the Log button
  const [tripContextTripName, setTripContextTripName] = useState<string | null>(null);
  const [tripContextSegmentName, setTripContextSegmentName] = useState<string | null>(null);
  const [tripContextParentName, setTripContextParentName] = useState<string | null>(null);
  // Whether this place is inside the one being logged under (a ride in the
  // park you're at). Null while checking. When it isn't (SFO while a hotel
  // visit is still open), logging it on its own comes first.
  const [insideContextParent, setInsideContextParent] = useState<boolean | null>(null);

  const canManage = userProfile?.roles?.includes("manage_entities");

  // Fetch children of this entity
  const { entities: allChildren, loading: childrenLoading } = useEntities(
    entityId ? { parentId: entityId } : {}
  );

  // In trip-context this page is a step in the log flow, so closed places drop
  // out of it; opened from the nav it's the management view and shows
  // everything, because editing last year's houses is the whole point of
  // having them. Same window as the log sheet that sent you here.
  const tripWindow = useActiveAsOf({
    tripId: tripContextTripId,
    segmentId: tripContextSegmentId,
  });
  // No trip to judge against means judging by today: an unbounded window
  // overlaps every entity, so asking it "is this still a place" always says
  // yes. Matches the browse page, which is the other way into this same list.
  const activeDuring = useMemo(
    () => (tripContextTripId ? tripWindow : nowWindow()),
    [tripContextTripId, tripWindow],
  );
  const { children, retiredIds, retiredCount } = useMemo(() => {
    const { active, retired } = partitionByActive(allChildren, activeDuring);
    if (tripContextTripId) {
      return { children: active, retiredIds: new Set<string>(), retiredCount: retired.length };
    }
    // Kept, but below what's still running — and the type grouping below
    // derives from this order, so they sink within their own type too.
    return {
      children: [...active, ...retired],
      retiredIds: new Set(retired.map((e) => e.id)),
      retiredCount: 0,
    };
  }, [allChildren, tripContextTripId, activeDuring]);

  // Group children by type for organized display
  const childrenByType = useMemo(() => {
    if (children.length === 0) return [];
    const typeMap = new Map<EntityType, typeof children>();
    for (const child of children) {
      const list = typeMap.get(child.type) ?? [];
      list.push(child);
      typeMap.set(child.type, list);
    }
    return Array.from(typeMap.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [children]);

  useEffect(() => {
    if (!entityId) return;

    const unsub = onSnapshot(doc(db, "entities", entityId), (snapshot) => {
      if (snapshot.exists()) {
        setEntity(withCurrentType({ id: snapshot.id, ...snapshot.data() } as EntityWithId));
      } else {
        setEntity(null);
      }
      setLoading(false);
    });

    return unsub;
  }, [entityId]);

  // Fetch parent name when entity loads
  useEffect(() => {
    if (!entity?.parentId) {
      setParentName(null);
      return;
    }
    getDoc(doc(db, "entities", entity.parentId)).then((snap) => {
      if (snap.exists()) {
        setParentName((snap.data() as Entity).displayName);
      }
    });
  }, [entity?.parentId]);

  // Fetch trip + segment names for the Log button label when in trip-context
  useEffect(() => {
    if (!tripContextTripId) { setTripContextTripName(null); return; }
    let cancelled = false;
    getDoc(doc(db, "trips", tripContextTripId)).then((snap) => {
      if (!cancelled && snap.exists()) setTripContextTripName((snap.data() as Trip).name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [tripContextTripId]);

  useEffect(() => {
    if (!tripContextTripId || !tripContextSegmentId) {
      setTripContextSegmentName(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "trips", tripContextTripId, "segments", tripContextSegmentId)).then((snap) => {
      if (!cancelled && snap.exists()) setTripContextSegmentName((snap.data() as Segment).name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [tripContextTripId, tripContextSegmentId]);

  // And the name of the place this is being logged inside of, so the CTA says
  // where it's going rather than just which trip.
  useEffect(() => {
    setTripContextParentName(null);
    setInsideContextParent(null);
    if (!tripContextParentId || !entityId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "interactions", tripContextParentId));
        if (cancelled) return;
        if (!snap.exists()) { setInsideContextParent(false); return; }
        const parent = snap.data() as Interaction;
        // A custom place (no entity, like a hotel typed in by name) holds
        // nothing from the places list.
        const inside = parent.entityId
          ? await isPlaceInside(entityId, parent.entityId, async (id) => {
              const e = await getDoc(doc(db, "entities", id));
              return e.exists() ? ((e.data() as Entity).parentId ?? null) : null;
            })
          : false;
        if (cancelled) return;
        setInsideContextParent(inside);
        if (parent.name) { setTripContextParentName(parent.name); return; }
        if (!parent.entityId) return;
        const parentEntity = await getDoc(doc(db, "entities", parent.entityId));
        if (!cancelled && parentEntity.exists()) {
          setTripContextParentName((parentEntity.data() as Entity).displayName);
        }
      } catch {
        // The label falls back to the trip/segment name; not worth surfacing.
        if (!cancelled) setInsideContextParent((v) => v ?? false);
      }
    })();
    return () => { cancelled = true; };
  }, [tripContextParentId, entityId]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // ThemeParks.wiki live data. These hooks must run unconditionally, before the
  // early returns below — they all no-op gracefully when there's no wiki link.
  const wikiParkExternalId =
    entity && entity.type === "PARK" && entity.externalSource === THEMEPARKS_WIKI_SOURCE && entity.externalId
      ? entity.externalId
      : null;
  const liveWait = useLiveWaitTime(
    entity && (entity.type === "ATTRACTION" || entity.type === "EXPERIENCE") ? entity : null
  );
  const parkWaitTimes = useParkWaitTimes(wikiParkExternalId);

  // Up: the parent place, else the trip being logged in, else all places.
  usePageBar({
    title: entity?.displayName ?? null,
    parent: !entity
      ? { to: "/entities", label: "Entities" }
      : entity.parentId
        ? { to: `/entities/${entity.parentId}${tripContextSearch}`, label: parentName ?? "Back" }
        : inTripContext && tripContextTripId
          ? {
              to: tripContextSegmentId
                ? `/trips/${tripContextTripId}/segments/${tripContextSegmentId}`
                : `/trips/${tripContextTripId}`,
              label: contextTripName ?? "Trip",
            }
          : { to: "/entities", label: "Entities" },
    cancel: editing ? { onClick: () => setEditing(false) } : null,
  });

  if (loading) {
    return <div className="loading">Loading entity...</div>;
  }

  if (!entity || !entityId) {
    return (
      <p>
        Entity not found. <Link to="/entities">Back to entities</Link>
      </p>
    );
  }

  const handleEditSubmit = async (data: {
    displayName: string;
    type: string;
    parentId: string | null;
    imageUrl: string;
    externalId: string;
    externalSource: string;
    activeFrom: string;
    activeTo: string;
    timeInputMode: string | null;
    eventOptions: import("../types").EventOption[];
    customQuestions: import("../types").CustomQuestion[];
  }) => {
    const activeFrom = data.activeFrom
      ? Timestamp.fromDate(new Date(data.activeFrom + "T00:00:00"))
      : null;
    const activeTo = data.activeTo
      ? Timestamp.fromDate(new Date(data.activeTo + "T00:00:00"))
      : null;
    const fields = {
      displayName: data.displayName,
      type: data.type as EntityType,
      parentId: data.parentId,
      imageUrl: data.imageUrl || null,
      externalId: data.externalId || null,
      externalSource: data.externalSource || null,
      activeFrom,
      activeTo,
      eventOptions: data.eventOptions,
    };
    // Reflect the edit locally right away — the page renders off the `entity`
    // snapshot, and Firestore coalesces the first write into a freshly-attached
    // listener's initial sync, so the change otherwise wouldn't show till refresh.
    setEntity((prev) =>
      prev
        ? {
            ...prev,
            ...fields,
            customConfig: {
              ...(prev.customConfig ?? {}),
              time_input_mode: data.timeInputMode ?? undefined,
              custom_questions: data.customQuestions,
            },
          }
        : prev,
    );
    fireWrite(updateDoc(doc(db, "entities", entityId), {
      ...fields,
      // Null follows the type's default, so it's cleared rather than stored.
      "customConfig.time_input_mode": data.timeInputMode ?? deleteField(),
      "customConfig.custom_questions": data.customQuestions,
      updatedAt: serverTimestamp(),
    }), "save entity");
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm("Delete this entity? This cannot be undone.")) return;
    setDeleting(true);
    fireWrite(deleteDoc(doc(db, "entities", entityId)), "delete entity");
    navigate("/entities");
  };

  /** `underParent` false logs the place on its own in the segment, even when
   *  the log flow was started from inside another place. */
  const handleLogHere = async (underParent: boolean) => {
    if (!inTripContext || !user || !tripContextTripId || logging) return;
    setLogging(true);
    setLogError(null);
    try {
      const id = await createInteractionAt({
        userId: user.uid,
        tripId: tripContextTripId,
        segmentId: tripContextSegmentId || null,
        parentInteractionId: underParent ? tripContextParentId || null : null,
        entityId,
        name: null,
      });
      navigate(`/trips/${tripContextTripId}/interactions/${id}`);
    } catch {
      setLogError("Failed to create activity. Please try again.");
      setLogging(false);
    }
  };

  if (editing) {
    return (
      <div className="page">
        <h1>Edit Entity</h1>
        <EntityForm
          initial={entity}
          onSubmit={handleEditSubmit}
          onCancel={() => setEditing(false)}
          submitLabel="Save Changes"
        />
      </div>
    );
  }

  const isActive = () => {
    const now = new Date();
    if (entity.activeFrom) {
      const from = entity.activeFrom.toDate();
      if (now < from) return "Not yet active";
    }
    if (entity.activeTo) {
      const to = entity.activeTo.toDate();
      if (now > to) return "Inactive";
    }
    return null;
  };

  const activeStatus = isActive();

  // Cancel destination + button label for the "Log Activity under …" CTA
  const cancelHref =
    inTripContext && tripContextTripId
      ? tripContextSegmentId
        ? `/trips/${tripContextTripId}/segments/${tripContextSegmentId}`
        : `/trips/${tripContextTripId}`
      : null;
  // Naming the place wins over naming the segment: "under Magic Kingdom" is
  // where the activity will actually sit.
  const segmentLabel = tripContextTripName
    ? tripContextSegmentName
      ? `${tripContextTripName} · ${tripContextSegmentName}`
      : tripContextTripName
    : "trip";
  const contextLabel = tripContextParentName ?? segmentLabel;
  // Started from inside another place: offer both, the likelier one first.
  const offerBoth = !!tripContextParentId;
  const ownFirst = offerBoth && insideContextParent === false;
  const ownLabel = `Log ${entity.displayName} on its own`;
  const underLabel = `Log Activity under ${contextLabel}`;

  const hasExtraDetails =
    !!(entity.externalId || entity.activeFrom || entity.activeTo || entity.location);

  const showLiveWaitChip =
    (entity.type === "ATTRACTION" || entity.type === "EXPERIENCE") && liveWait.linked;

  return (
    <div className="page">
      {entity.imageUrl && (
        <img
          src={entity.imageUrl}
          alt={entity.displayName}
          className="entity-hero-image"
        />
      )}

      <div className="detail-header">
        <h1>
          {entity.displayName}
          {activeStatus && (
            <span className="status-badge status-pending" style={{ marginLeft: "0.5rem", fontSize: "0.75rem", verticalAlign: "middle" }}>
              {activeStatus}
            </span>
          )}
        </h1>
        {canManage && (
          <div className="detail-menu" ref={menuRef}>
            <button
              className="detail-menu-trigger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Actions"
            >
              &#8942;
            </button>
            {menuOpen && (
              <div className="detail-menu-dropdown">
                <button onClick={() => { setEditing(true); setMenuOpen(false); }}>
                  Edit
                </button>
                <Link to={`/entities/new?parentId=${entityId}`} onClick={() => setMenuOpen(false)}>
                  Add Child
                </Link>
                <button
                  className="detail-menu-danger"
                  onClick={() => { setMenuOpen(false); handleDelete(); }}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showLiveWaitChip && (
        <div style={{ margin: "0.25rem 0 0.75rem" }}>
          <LiveWaitChip
            waitMinutes={liveWait.waitMinutes}
            status={liveWait.status}
            showtimes={liveWait.showtimes}
            stale={liveWait.stale}
            fetchedAt={liveWait.fetchedAt}
            loading={liveWait.loading}
          />
        </div>
      )}

      {wikiParkExternalId && <ParkScheduleCard parkExternalId={wikiParkExternalId} />}

      {inTripContext && (
        <div className="log-activity-cta">
          {logError && <div className="form-error">{logError}</div>}
          <button
            type="button"
            className="btn btn-block"
            onClick={() => handleLogHere(!ownFirst)}
            disabled={logging || !user || (offerBoth && insideContextParent === null)}
          >
            {logging ? "Logging…" : ownFirst ? ownLabel : underLabel}
          </button>
          {offerBoth && insideContextParent !== null && (
            <button
              type="button"
              className="btn btn-block btn-secondary log-activity-alt"
              onClick={() => handleLogHere(ownFirst)}
              disabled={logging || !user}
            >
              {ownFirst ? underLabel : "Log on its own instead"}
            </button>
          )}
          {cancelHref && (
            <Link to={cancelHref} className="btn btn-small btn-secondary log-activity-cancel">Cancel</Link>
          )}
        </div>
      )}

      {/* Children: tapping any child always navigates to its detail page,
          preserving trip-context. From there the user explicitly logs via
          the "Log Activity under …" button. */}
      <section>
        <h2>Children ({children.length})</h2>

        {childrenLoading ? (
          <div className="loading">Loading...</div>
        ) : children.length === 0 ? (
          <p className="text-muted">
            {retiredCount > 0
              ? "Nothing here is running during this trip."
              : "No child entities."}
          </p>
        ) : childrenByType.length === 1 ? (
          <ul className="entity-list">
            {children.map((child) => (
              <li key={child.id} className={`entity-card${retiredIds.has(child.id) ? " entity-card-retired" : ""}`}>
                <div className="entity-card-content">
                  {child.imageUrl ? (
                    <img src={child.imageUrl} alt="" className="entity-card-thumb" />
                  ) : (
                    <div className="entity-card-thumb entity-card-thumb-empty" />
                  )}
                  <div className="entity-card-info">
                    <h3>
                      <Link to={`/entities/${child.id}${tripContextSearch}`} className="entity-card-link">
                        {child.displayName}
                      </Link>
                    </h3>
                    <span className="entity-type-badge">{typeLabel(child.type)}</span>
                    {retiredIds.has(child.id) && <span className="entity-retired-badge">Closed</span>}
                    {wikiParkExternalId && <ChildWaitBadge child={child} parkWaitTimes={parkWaitTimes} />}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="entity-groups">
            {childrenByType.map(([type, kids]) => (
              <div key={type} className="entity-child-group">
                <h4 className="entity-group-label">{typeLabel(type)}s</h4>
                <ul className="entity-list">
                  {kids.map((child) => (
                    <li key={child.id} className={`entity-card${retiredIds.has(child.id) ? " entity-card-retired" : ""}`}>
                      <div className="entity-card-content">
                        {child.imageUrl ? (
                          <img src={child.imageUrl} alt="" className="entity-card-thumb" />
                        ) : (
                          <div className="entity-card-thumb entity-card-thumb-empty" />
                        )}
                        <div className="entity-card-info">
                          <h3>
                            <Link to={`/entities/${child.id}${tripContextSearch}`} className="entity-card-link">
                              {child.displayName}
                            </Link>
                          </h3>
                          {retiredIds.has(child.id) && <span className="entity-retired-badge">Closed</span>}
                          {wikiParkExternalId && <ChildWaitBadge child={child} parkWaitTimes={parkWaitTimes} />}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* What's been logged here before, across the reader's trips: the
          numbers by access tier, and each visit as it was written down. */}
      <EntityHistory entityId={entityId} entity={entity} />

      {hasExtraDetails && (
        <details className="entity-extra">
          <summary>Details</summary>
          <dl className="detail-list">
            {entity.externalId && entity.externalSource && (
              <>
                <dt>{entity.externalSource === THEMEPARKS_WIKI_SOURCE ? "ThemeParks.wiki" : entity.externalSource}</dt>
                <dd>{entity.externalId}</dd>
              </>
            )}
            {(entity.activeFrom || entity.activeTo) && (
              <>
                <dt>Active</dt>
                <dd className="detail-date-range">
                  {entity.activeFrom && (
                    <span>{entity.activeFrom.toDate().toLocaleDateString()}</span>
                  )}
                  {entity.activeFrom && entity.activeTo && <span> &ndash; </span>}
                  {entity.activeTo && (
                    <span>{entity.activeTo.toDate().toLocaleDateString()}</span>
                  )}
                </dd>
              </>
            )}
            {entity.location && (
              <>
                <dt>Location</dt>
                <dd>
                  {entity.location.latitude}, {entity.location.longitude}
                </dd>
              </>
            )}
          </dl>
        </details>
      )}
    </div>
  );
}
